/**
 * Pagination — Reusable paging control.
 *
 * Usage:
 *   <Pagination total={total} page={page} pageSize={50} onChange={setPage} />
 *
 * - Auto-hides when total <= pageSize
 * - Shows "1 – 50 / 247 toplam" info
 * - Sliding 5-page window
 */
import React from "react";

export default function Pagination({ total, page, pageSize = 50, onChange, label = "kayıt" }) {
    if (!total || total <= pageSize) return null;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const safePage = Math.max(1, Math.min(page || 1, totalPages));
    const start = (safePage - 1) * pageSize + 1;
    const end = Math.min(safePage * pageSize, total);
    const windowStart = Math.max(1, Math.min(safePage - 2, totalPages - 4));
    const windowEnd = Math.min(totalPages, windowStart + 4);
    const pages = [];
    for (let i = windowStart; i <= windowEnd; i++) pages.push(i);
    const go = (p) => onChange && onChange(Math.max(1, Math.min(totalPages, p)));
    return (
        <div data-testid="pagination" className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-800 flex-wrap">
            <div className="text-xs text-slate-500">
                <span className="font-semibold">{start}</span><span> – </span><span className="font-semibold">{end}</span><span> arası gösteriliyor · </span>
                <span className="font-semibold">{total}</span><span> toplam {label}</span>
            </div>
            <div className="flex items-center gap-1">
                <button data-testid="page-first" onClick={() => go(1)} disabled={safePage === 1} className="px-2 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800">« İlk</button>
                <button data-testid="page-prev" onClick={() => go(safePage - 1)} disabled={safePage === 1} className="px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800">‹ Önceki</button>
                {pages.map((p) => (
                    <button key={p} data-testid={`page-${p}`} onClick={() => go(p)}
                        className={`min-w-[32px] px-2 py-1.5 rounded-lg text-xs font-bold ${p === safePage ? "bg-sky-500 text-white" : "hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"}`}>{p}</button>
                ))}
                <button data-testid="page-next" onClick={() => go(safePage + 1)} disabled={safePage >= totalPages} className="px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800">Sonraki ›</button>
                <button data-testid="page-last" onClick={() => go(totalPages)} disabled={safePage >= totalPages} className="px-2 py-1.5 rounded-lg text-xs font-bold disabled:opacity-30 hover:bg-slate-100 dark:hover:bg-slate-800">Son »</button>
            </div>
        </div>
    );
}
